import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { LayoutTemplate } from "lucide-react";
import { useGet } from "../lib/hooks";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { useAccess } from "../lib/access";
import { PersonalDashboard } from "../components/dashboard/PersonalDashboard";
import { OnboardingChecklist } from "../components/Onboarding";

function ViewPicker({ list, value, onChange }) {
  return (
    <div className="relative z-20 mb-6 flex flex-wrap items-center justify-end gap-2" data-testid="dashboard-view-picker">
      <span className="text-xs text-slate-400">Viewing</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="h-9 min-w-[240px] rounded-md border border-[#C2A462]/40 bg-[#0f1a2b] px-3 text-sm text-slate-100" data-testid="dashboard-view-select">
        <option value="">My assigned dashboard</option>
        {list.filter((d) => !d.is_template).length > 0 && <optgroup label="Dashboards">{list.filter((d) => !d.is_template).map((d) => <option key={d.id} value={d.id}>{d.name || "Untitled"}</option>)}</optgroup>}
        {list.some((d) => d.is_template) && <optgroup label="Templates">{list.filter((d) => d.is_template).map((d) => <option key={d.id} value={d.id}>{d.name || "Untitled"} (template)</option>)}</optgroup>}
      </select>
    </div>
  );
}

export default function Dashboard() {
  const { user, account } = useAuth();
  const { nav } = useAccess();
  const [params, setParams] = useSearchParams();
  const [mine, setMine] = useState(undefined);
  const [d] = useGet("/dashboard");
  const manage = nav?.lab ? user.role === "agency_owner" : nav?.manager;
  const [list] = useGet(manage ? "/dashboards" : null);
  const key = `bos_view_dashboard:${account?.id || ""}`;
  const [view, setViewRaw] = useState(() => params.get("view") || localStorage.getItem(key) || "");
  const setView = (v) => { v ? localStorage.setItem(key, v) : localStorage.removeItem(key); setViewRaw(v); params.delete("view"); setParams(params, { replace: true }); };
  useEffect(() => { api.get("/dashboards/mine").then((r) => setMine(r.data)).catch(() => setMine(null)); }, []);
  if (mine === undefined) return <div className="text-muted-foreground">Loading dashboard…</div>;
  const picked = manage && view ? (list || []).find((x) => x.id === view) : null;
  const shown = picked || mine;
  const link = manage && <Link to={nav?.lab ? "/app/dashboards" : "/app/settings?tab=features"} className="mt-6 inline-flex items-center gap-2 text-xs text-[#C2A462] hover:text-[#EBD6A0]" data-testid="customise-dashboard-link"><LayoutTemplate className="h-3.5 w-3.5" />{nav?.lab ? "Open Features Lab" : "Open Features Builder"}</Link>;
  const picker = manage && (list || []).length > 0 && <ViewPicker list={list} value={picked ? view : ""} onChange={setView} />;
  if (shown) return <div data-testid="dashboard-page">{picker}<PersonalDashboard key={shown.id} dashboard={shown} data={d} slot={<OnboardingChecklist key="onboarding" />} />{link}</div>;
  return (
    <div data-testid="dashboard-page">
      {picker}
      <div className="mb-6"><OnboardingChecklist /></div>
      <div className="max-w-xl mt-10 rounded-xl border border-[#C2A462]/30 bg-[#0f1a2b] p-8" data-testid="dashboard-being-set-up">
        <LayoutTemplate className="h-6 w-6 text-[#C2A462]" strokeWidth={1.6} />
        <div className="mt-3 text-lg font-semibold text-slate-100">{manage && (list || []).length ? "No dashboard is assigned to you" : "Your dashboard is being set up"}</div>
        <p className="mt-1 text-sm text-slate-400">{manage && (list || []).length ? "Choose a dashboard above to view it, or assign one to yourself in the Features Lab." : "Your administrator will make your dashboard available soon."}</p>
        {link}
      </div>
    </div>
  );
}
